import useAxios from 'hooks/useAxios'
import React, { useState, useRef, useEffect } from 'react'
import { Form, Button, Table, Checkbox } from 'semantic-ui-react'
import styled from 'styled-components'
import { toast } from 'libs/toast'
import { validateRequiredText } from 'libs/form-validation'
import ErrorText from 'shared/components/forms/ErrorText'
import { ALL_API_KEY_SCOPES } from './constants'
import { API_KEY_ACTION_NAME } from 'hooks/useSaveUserActivityLog'

const ScopesTable = styled(Table)`
  &.ui.table {
    border-color: ${({ $hasError }) =>
      $hasError ? 'var(--ss-negative-border-color)' : undefined} !important;
    ${({ $hasError }) => $hasError && 'margin-bottom: 0 !important;'}
  }
`

export default function ApiKeyFormModal({
  modal: Modal,
  close,
  handleCreateApiKey,
  handleUpdateApiKey,
  setSelectedEditApiKeyId,
  selectedApiKey = null,
  saveUserActivityLog,
  convertApiKeyToLogFormat,
  companyId,
}) {
  const [name, setName] = useState('')
  const [isFullAccess, setIsFullAccess] = useState(false)
  const [scopes, setScopes] = useState([])
  const [errors, setErrors] = useState({ name: '', scopes: '' })

  const hiddenRefSubmitButton = useRef(null)
  const duplicateNameRef = useRef('')
  const isEdit = Boolean(selectedApiKey)

  const scopeRows = [...new Set(ALL_API_KEY_SCOPES.map((s) => s.split(':')[0]))].map(
    (resource) => ({
      label: resource.charAt(0).toUpperCase() + resource.slice(1).replace('-', ' '),
      resource,
      hasWrite: ALL_API_KEY_SCOPES.includes(`${resource}:write`),
    })
  )

  const { fetch: submitApiKey, loading: loadingSubmitApiKey } = useAxios({
    method: 'post',
    url: `account/api-keys/`,
    skip: true,
  })

  useEffect(() => {
    if (selectedApiKey) {
      setName(selectedApiKey.name)
      setIsFullAccess(selectedApiKey.is_full_access)
      setScopes(selectedApiKey.scopes)
    } else {
      setIsFullAccess(false)
      setScopes([])
      setName('')
      setSelectedEditApiKeyId(null)
    }

    return () => {
      setIsFullAccess(false)
      setScopes([])
      setName('')
      setSelectedEditApiKeyId(null)
    }
  }, [selectedApiKey, setSelectedEditApiKeyId])

  function validateName() {
    const requiredError = validateRequiredText('Key name', name)
    if (requiredError) {
      return requiredError
    }
    if (duplicateNameRef.current && name === duplicateNameRef.current) {
      return 'Key name already exists. Use a different name to continue.'
    }
    return ''
  }

  function validateForm() {
    return {
      name: validateName(),
      scopes: !isFullAccess && scopes.length < 1 ? 'Select at least one scope to continue.' : '',
    }
  }

  function handlePostSubmitApiKey(apiKeyResponseData, isEditMode, logData) {
    if (isEditMode) {
      toast.success('API key updated successfully.')
      handleUpdateApiKey(apiKeyResponseData)
      saveUserActivityLog({
        action: API_KEY_ACTION_NAME.updated_api_key,
        statusCode: 200,
        requestBody: logData,
        target: `com_${companyId}`,
      })
    } else {
      toast.success('API key created successfully.')
      handleCreateApiKey(apiKeyResponseData.api_key, apiKeyResponseData.token)
      saveUserActivityLog({
        action: API_KEY_ACTION_NAME.created_api_key,
        statusCode: 201,
        requestBody: logData,
        target: `com_${companyId}`,
      })
    }
    close()
  }

  function isDuplicateNameError(error) {
    const data = error?.response?.data
    if (!data) {
      return false
    }
    return Boolean(data.name)
  }

  function handleScopeChange(scope, checked) {
    setScopes((prev) => (checked ? [...prev, scope] : prev.filter((s) => s !== scope)))
    setErrors((prev) => ({ ...prev, scopes: '' }))
  }

  function handleSubmit(e) {
    e.preventDefault()

    const validationErrors = validateForm()
    setErrors(validationErrors)

    if (validationErrors.name || validationErrors.scopes) {
      return
    }

    const data = {
      name: name,
      is_full_access: isFullAccess,
      scopes: scopes,
    }

    const url = isEdit ? `account/api-keys/${selectedApiKey.id}` : 'account/api-keys/'

    submitApiKey({
      method: isEdit ? 'patch' : 'post',
      url: url,
      data: data,
    })
      .then((res) => {
        const logData = convertApiKeyToLogFormat(isEdit ? res.data : res.data.api_key)
        handlePostSubmitApiKey(res.data, isEdit, logData)
      })
      .catch((error) => {
        if (isDuplicateNameError(error)) {
          duplicateNameRef.current = name
          setErrors((prev) => ({
            ...prev,
            name: 'Key name already exists. Use a different name to continue.',
          }))
        } else {
          toast.error(
            `${
              isEdit ? 'Could not save changes.' : 'Could not create API key.'
            } Contact support for help.`
          )
        }
        console.error(error)
        saveUserActivityLog({
          action: isEdit
            ? API_KEY_ACTION_NAME.updated_api_key
            : API_KEY_ACTION_NAME.created_api_key,
          statusCode: error.response.status,
          errorResponseBody: data,
          target: `com_${companyId}`,
        })
      })
  }

  return (
    <React.Fragment>
      <Modal size="tiny">
        <Modal.Header>{isEdit ? 'Edit API key' : 'Create API key'}</Modal.Header>
        <Modal.Content>
          <Form onSubmit={handleSubmit}>
            <input type="submit" hidden ref={hiddenRefSubmitButton} />
            <Form.Field error={Boolean(errors.name)}>
              <label>Key name</label>
              <input
                value={name}
                onChange={(e) => {
                  setName(e.target.value)
                  setErrors((prev) => ({ ...prev, name: '' }))
                }}
                placeholder="Use a name that helps you recognize where this key is used."
              />
              {errors.name && <ErrorText>{errors.name}</ErrorText>}
            </Form.Field>
            <Form.Field>
              <label>Permissions</label>
              <p className="subtext">
                {isFullAccess
                  ? 'Full access automatically includes all current and future scopes.'
                  : 'Choose which scopes this key can access. New scopes are not included automatically.'}
              </p>
              <Button.Group basic fluid widths={2} size="small">
                <Button type="button" active={isFullAccess} onClick={() => setIsFullAccess(true)}>
                  Full access
                </Button>
                <Button type="button" active={!isFullAccess} onClick={() => setIsFullAccess(false)}>
                  Custom
                </Button>
              </Button.Group>
            </Form.Field>

            {!isFullAccess && (
              <Form.Field>
                <ScopesTable
                  compact
                  basic
                  className="list-table default-header-bg"
                  $hasError={Boolean(errors.scopes)}
                >
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Scopes</Table.HeaderCell>
                      <Table.HeaderCell textAlign="center">Read</Table.HeaderCell>
                      <Table.HeaderCell textAlign="center">Write</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {scopeRows.map(({ label, resource, hasWrite }) => (
                      <Table.Row key={resource}>
                        <Table.Cell>{label}</Table.Cell>
                        <Table.Cell textAlign="center">
                          <Checkbox
                            checked={scopes.includes(`${resource}:read`)}
                            onChange={(_, { checked }) =>
                              handleScopeChange(`${resource}:read`, checked)
                            }
                          />
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          {hasWrite && (
                            <Checkbox
                              checked={scopes.includes(`${resource}:write`)}
                              onChange={(_, { checked }) =>
                                handleScopeChange(`${resource}:write`, checked)
                              }
                            />
                          )}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </ScopesTable>
                {errors.scopes && <ErrorText>{errors.scopes}</ErrorText>}
              </Form.Field>
            )}
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={close} disabled={loadingSubmitApiKey}>
            Cancel
          </Button>
          <Button
            type="submit"
            primary
            loading={loadingSubmitApiKey}
            disabled={loadingSubmitApiKey}
            onClick={() => hiddenRefSubmitButton.current?.click()}
          >
            {isEdit ? 'Save changes' : 'Create API key'}
          </Button>
        </Modal.Actions>
      </Modal>
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/pages/Admin/ApiKey/ApiKeyFormModal.js